import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadAvatarService } from 'features/users/services';
import { notification } from 'features/notifications/toast/notification';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectLocalUserId } from 'features/users/usersSlice';
import { selectMainRoomId } from 'features/breakout-rooms/breakoutRoomsSlice';
import { avatarDeleteRequested, avatarUploadSucceeded } from 'features/users/actions';

export const useAvatarUpload = () => {
  const { t } = useTranslation('notifications');
  const dispatch = useAppDispatch();
  const roomId = useAppSelector(selectMainRoomId);
  const userId = useAppSelector(selectLocalUserId);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File): Promise<boolean> => {
      setUploading(true);
      setError(null);

      try {
        const { url } = await uploadAvatarService(file, roomId, userId);
        dispatch(avatarUploadSucceeded({ url }));

        return true;
      } catch {
        const errorMessage = t('avatar_upload_error');
        setError(errorMessage);
        notification.error(errorMessage);

        return false;
      } finally {
        setUploading(false);
      }
    },
    [t, dispatch, roomId, userId]
  );

  const deleteAvatar = useCallback(() => {
    dispatch(avatarDeleteRequested());
  }, [dispatch]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { uploading, error, upload, deleteAvatar, clearError };
};
